import { TreeItem } from '@dealstack/frontend/shared/components/Tree/utils';
import {
	ClassificationGroupType,
	DealClassifierGroupRes,
} from '@dealstack/shared/lib/backendClient';
import { groupBy } from 'lodash';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import {
	DocumentsLibraryDocumentRes,
	SelectedDocument,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';

export type TreeItemsWithMeta = TreeItem & {
	dealClassifierId?: string | null;
	groupType?: ClassificationGroupType;
};

export const toSelectedDocument = (row: DocumentsLibraryDocumentRes): SelectedDocument => ({
	documentId: row.id,
	fileId: row.fileId,
	documentName: row.name,
});

// converts the single dimension array into the nested tree structure that the tree expects
export const convertApiResponseToTree = (
	apiResponse: DealClassifierGroupRes[] = [],
	expandedTreeIds: TreeItem['id'][] = [],
	selectedTreeIds: TreeItem['id'][] = [],
	translations: (s: string) => string
): TreeItemsWithMeta[] => {
	const folders = groupBy(apiResponse, 'groupType');
	const completedDocs = Object.keys(folders)
		.filter((grouping) => grouping === ClassificationGroupType.COMPLETED_DOCUMENTS)
		.map((folderType, index) => {
			const children = buildDocumentTree(
				folders[folderType],
				expandedTreeIds,
				selectedTreeIds
			);

			const folderId = createFolderId(folderType, index);
			const folderName = folders[folderType][index]?.groupName;
			const groupType = folders[folderType][index]?.groupType;

			return {
				id: folderId,
				name: translations(folderName),
				icon: 'folder' as PredefinedIcons,
				isFolder: true,
				selected: selectedTreeIds.includes(folderId),
				expanded: expandedTreeIds.includes(folderId),
				children,
				groupType,
			};
		});

	const unclassifiedDocs = Object.keys(folders)
		.filter((grouping) => grouping === ClassificationGroupType.UNCLASSIFIED)
		.map((folderType, index) => {
			const folderId = createFolderId(folderType, index);
			const folderName = folders[folderType][index]?.groupName;
			const groupType = folders[folderType][index]?.groupType;

			return {
				id: folderId,
				name: translations(folderName),
				icon: 'folder' as PredefinedIcons,
				isFolder: true,
				selected: selectedTreeIds.includes(folderId),
				expanded: expandedTreeIds.includes(folderId),
				groupType,
			};
		});

	return [...completedDocs, ...unclassifiedDocs];
};

export const createFolderId = (folderSuffix: string, folderIndex: number) =>
	`${folderSuffix}_${folderIndex}`;

const buildDocumentTree = (
	dealClassifiers: DealClassifierGroupRes[],
	expandedTreeIds: TreeItem['id'][] = [],
	selectedTreeIds: TreeItem['id'][] = []
): TreeItemsWithMeta[] => {
	// create a map of all documents by their classifierId for quick lookup
	const documentMap = new Map<DealClassifierGroupRes['classifierId'], TreeItemsWithMeta>();
	dealClassifiers.forEach((doc) => {
		documentMap.set(doc.classifierId, {
			id: doc.classifierId,
			dealClassifierId: doc.dealClassifierId,
			name: doc.name,
			icon: 'group' as PredefinedIcons,
			isFolder: false,
			selected: selectedTreeIds.includes(doc.classifierId),
			expanded: expandedTreeIds.includes(doc.classifierId),
		});
	});

	// find root nodes (those without parents) and build the tree
	const rootNodes: TreeItemsWithMeta[] = [];

	dealClassifiers.forEach((doc) => {
		const node = documentMap.get(doc.classifierId);
		if (!node) {
			return;
		}

		if (!doc.classifierParentId) {
			// this is a root node
			rootNodes.push(node);
		} else {
			// this is a child node, add it to its parent's children
			const parent = documentMap.get(doc.classifierParentId);

			if (!parent) {
				return;
			}

			node.selected = selectedTreeIds.includes(node.id);
			node.expanded = expandedTreeIds.includes(node.id);

			if (!Array.isArray(parent?.children)) {
				parent.children = [];
			}

			parent.children.push(node);
			parent.isFolder = true;
			parent.expanded = expandedTreeIds.includes(parent.id);
			parent.icon = 'folder' as PredefinedIcons;

			documentMap.set(doc.classifierParentId, parent);
		}
	});

	return rootNodes;
};
